/** EBC Admin auth context — current user + token + login/logout. */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { adminApi, setAdminToken, getAdminToken, formatApiErrorDetail } from './api';

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  // status: 'checking' | 'authed' | 'guest'
  const [status, setStatus] = useState('checking');
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    const token = getAdminToken();
    if (!token) {
      setStatus('guest');
      setUser(null);
      return;
    }
    try {
      const { data } = await adminApi.get('/auth/me');
      setUser(data);
      setStatus('authed');
    } catch {
      setAdminToken(null);
      setUser(null);
      setStatus('guest');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await adminApi.post('/auth/login', { email, password });
      setAdminToken(data.token);
      setUser(data.user);
      setStatus('authed');
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e?.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try { await adminApi.post('/auth/logout'); } catch { /* ignore */ }
    setAdminToken(null);
    setUser(null);
    setStatus('guest');
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await adminApi.post('/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword,
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e?.response?.data?.detail) };
    }
  };

  return (
    <AdminAuthContext.Provider value={{ status, user, login, logout, changePassword, refresh }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within <AdminAuthProvider>');
  return ctx;
};
